import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";

import type { PatientSelectorPatientSummaryV2 } from "../../PatientSelector";

export interface CompletedVisitHeaderProps {
  patient?: PatientSelectorPatientSummaryV2 | null;
  /** Visit crumb from `EncounterEntity.visitDateTimeLabel`. */
  visitLabel?: string | null;
  /**
   * When set, renders a Back control on the same row as the patient info —
   * the patient-details header pattern — so the completed note does not need
   * a separate app-chrome back row above it.
   */
  backTo?: string;
}

/**
 * The prototype also puts a full-screen toggle in this row. `VisitNoteEditor`
 * already owns that control, so it is not repeated here.
 */
export function CompletedVisitHeader({
  patient,
  visitLabel,
  backTo,
}: CompletedVisitHeaderProps) {
  const demographics = patient?.patientLine ?? patient?.metadata;
  const metaLine = [demographics, visitLabel ? `Visit · ${visitLabel}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <HorizontalFlexbox
      alignItems="center"
      className="shrink-0 gap-2.5 border-border border-b bg-background px-[18px] py-3.5 md-stitch:px-6"
    >
      {backTo ? (
        <Button
          type="button"
          asChild
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 gap-1.5 px-2 text-[12.5px] font-medium text-muted-foreground"
        >
          <Link to={backTo} replace>
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back
          </Link>
        </Button>
      ) : null}

      <VerticalFlexbox className="min-w-0 gap-0.5 leading-tight">
        <p className="truncate text-[13.5px] font-semibold tracking-[-0.01em] text-foreground">
          {patient?.name ?? "Completed visit"}
        </p>
        {metaLine ? (
          <p className="truncate font-mono text-[10.5px] text-muted-foreground">
            {metaLine}
          </p>
        ) : null}
      </VerticalFlexbox>
    </HorizontalFlexbox>
  );
}
